import cloneDeep from 'lodash/cloneDeep';

import type { ADFEntity } from '@atlaskit/adf-utils/types';
import { traverse } from '@atlaskit/adf-utils/traverse/main';
import type { ADNode } from '@atlaskit/editor-common/utils/validator';

import { expVal } from '@confluence/feature-experiments';
import { getLogger } from '@confluence/logger';
import { getHeadingId } from '@confluence/editor-heading-links/entry-points/headingLinksFromElements.ts';

const logger = getLogger('table-of-contents');

const INCLUDED_CONTENT_EXTENSION_KEYS = new Set(['excerpt-include', 'include-page', 'include']);
const EXCERPT_EXTENSION_KEY = 'excerpt';
const INCLUDED_HEADING_ID_DELIMITER = '-';

/**
 * Module-level WeakMap cache so multiple TOC macros on the same page share
 * a single ADF traversal.
 */
const headingsCache = new WeakMap<object, Map<string, ADFEntity[]>>();

const buildCacheKey = (
	minConfig: number,
	maxConfig: number,
	includeRegex: string | undefined,
	excludeRegex: string | undefined,
) => `${minConfig}|${maxConfig}|${includeRegex ?? ''}|${excludeRegex ?? ''}`;

export type IncludedContentMacroKey = 'excerpt-include' | 'include-page' | 'include';

export type IncludedContentReference = {
	key: string;
	extensionKey: IncludedContentMacroKey;
	macroId: string;
	contentId?: string;
	spaceKey?: string;
	// The host page's space key (the page containing the macro). The
	// page-include query uses this as the host-space fallback for resolving
	// space-less legacy targets, so it must match what PageIncludeMacroRenderer
	// passes (macroDefaultProps.spaceKey) to share the same Apollo cache key.
	hostSpaceKey?: string;
	pageTitle?: string;
	excerptName?: string;
	// Captured for excerpt-include subscriptions so the TOC can call
	// useExperimentalMacroMetadataQuery with the exact same variables
	// ExcerptIncludeImpl uses (same Apollo cache key → no extra request).
	parentContentId?: string;
	extensionType?: string;
	nodeType?: string;
	macroMetadata?: unknown;
	macroParams?: unknown;
};

export type IncludedContentMap = Map<string, ADFEntity[]>;

/**
 * A reference to a synced block instance on the page. `localId` is the
 * per-instance id from the `syncBlock` node's attrs; `resourceId` identifies the
 * source block. `localId` is used as BOTH the map key and the heading-id prefix
 * so that the same source block embedded twice on a page produces distinct,
 * stable heading ids that match what the nested synced-block renderer emits.
 */
export type SyncBlockReference = {
	localId: string;
	resourceId: string;
};

/**
 * Resolved synced-block content keyed by the reference node's `localId`.
 * Populated by the data-layer hook (`useSyncBlockTocHeadings`) and consumed by
 * the `syncBlock` visitor below.
 */
export type SyncBlockContentMap = Map<string, ADFEntity[]>;

type MacroParams = Record<string, { value?: string } | undefined> | undefined;

const getMacroParams = (node: ADFEntity): MacroParams => node?.attrs?.parameters?.macroParams;

const getParamValue = (macroParams: MacroParams, paramName: string): string | undefined =>
	macroParams?.[paramName]?.value?.trim() || undefined;

const getMacroId = (node: ADFEntity): string | undefined =>
	node?.attrs?.parameters?.macroMetadata?.macroId?.value || node?.attrs?.localId;

const extractContentIdFromTypedValue = (value: string): string | undefined =>
	value.match(/confluence-content:\/\/[^/]+\/(\d+)/)?.[1];

const parseLegacyLocation = (
	location: string | undefined,
): { spaceKey?: string; pageTitle?: string } => {
	if (!location) {
		return {};
	}

	const colonIdx = location.indexOf(':');
	if (colonIdx > 0) {
		return {
			spaceKey: location.slice(0, colonIdx).trim() || undefined,
			pageTitle: location.slice(colonIdx + 1).trim() || undefined,
		};
	}

	return { pageTitle: location.trim() || undefined };
};

const buildIncludedContentKey = ({
	extensionKey,
	contentId,
	spaceKey,
	pageTitle,
	excerptName,
}: Omit<IncludedContentReference, 'key' | 'macroId'>) =>
	[extensionKey, contentId ?? '', spaceKey ?? '', pageTitle ?? '', excerptName ?? ''].join('|');

const isIncludedContentNode = (node: ADFEntity): boolean =>
	INCLUDED_CONTENT_EXTENSION_KEYS.has(node?.attrs?.extensionKey);

export const getCompositeHeadingId = (
	macroId: string,
	headingId: string | undefined,
): string | undefined =>
	headingId ? `${macroId}${INCLUDED_HEADING_ID_DELIMITER}${headingId}` : undefined;

const extractIncludedContentReferenceFromNode = (
	node: ADFEntity,
	defaultSpaceKey?: string,
	parentContentId?: string,
): IncludedContentReference | undefined => {
	const extensionKey = node?.attrs?.extensionKey;
	if (
		extensionKey !== 'excerpt-include' &&
		extensionKey !== 'include-page' &&
		extensionKey !== 'include'
	) {
		return undefined;
	}

	const macroId = getMacroId(node);
	if (!macroId) {
		return undefined;
	}

	const macroParams = getMacroParams(node);
	const referencedContentId = getParamValue(macroParams, '_referencedContentId');
	const typedTarget = getParamValue(macroParams, '');
	const contentId =
		referencedContentId || (typedTarget && extractContentIdFromTypedValue(typedTarget));

	const legacyTarget =
		extensionKey === 'include-page' || extensionKey === 'include'
			? getParamValue(macroParams, 'pageTitle') || getParamValue(macroParams, '0') || typedTarget
			: typedTarget || getParamValue(macroParams, '0');
	const parsedLegacyTarget = parseLegacyLocation(legacyTarget);
	const spaceKey =
		getParamValue(macroParams, 'spaceKey') || parsedLegacyTarget.spaceKey || defaultSpaceKey;
	const pageTitle = parsedLegacyTarget.pageTitle;
	const excerptName =
		extensionKey === 'excerpt-include' ? getParamValue(macroParams, 'name') : undefined;

	if (!contentId && !pageTitle) {
		return undefined;
	}

	const key = buildIncludedContentKey({
		extensionKey,
		contentId,
		spaceKey,
		pageTitle,
		excerptName,
	});

	return {
		key,
		extensionKey,
		macroId,
		contentId,
		spaceKey,
		hostSpaceKey: defaultSpaceKey,
		pageTitle,
		excerptName,
		parentContentId,
		extensionType: node?.attrs?.extensionType,
		nodeType: node?.type,
		macroMetadata: node?.attrs?.parameters?.macroMetadata,
		macroParams,
	};
};

export const extractIncludedContentReferences = (
	adf: ADNode | ADFEntity | string | null | undefined,
	defaultSpaceKey?: string,
	parentContentId?: string,
): IncludedContentReference[] => {
	let adfObject = adf as ADFEntity;
	if (typeof adf === 'string') {
		try {
			adfObject = JSON.parse(adf);
		} catch {
			return [];
		}
	}

	const referencesByKey = new Map<string, IncludedContentReference>();
	traverse((adfObject || {}) as ADFEntity, {
		extension: (node) => {
			if (extractIncludedContentFromMacroOutput(node)) {
				return;
			}

			const reference = extractIncludedContentReferenceFromNode(
				node,
				defaultSpaceKey,
				parentContentId,
			);
			if (reference) {
				referencesByKey.set(reference.key, reference);
			}
		},
		bodiedExtension: (node) => {
			if (extractIncludedContentFromMacroOutput(node)) {
				return;
			}

			const reference = extractIncludedContentReferenceFromNode(
				node,
				defaultSpaceKey,
				parentContentId,
			);
			if (reference) {
				referencesByKey.set(reference.key, reference);
			}
		},
	});

	return Array.from(referencesByKey.values());
};

/**
 * Collect all reference synced-block nodes (`syncBlock`) anywhere in the
 * document. Deep-traverses (so sync blocks nested inside layouts, expands, etc.
 * are found), unlike the top-level-only `getSyncBlockNodesFromDoc`. Deduped by
 * `localId`. Source `bodiedSyncBlock` nodes are intentionally ignored — their
 * content is rendered inline and handled by the normal `heading` visitor.
 */
export const extractSyncBlockReferences = (
	adf: ADNode | ADFEntity | string | null | undefined,
): SyncBlockReference[] => {
	let adfObject = adf as ADFEntity;
	if (typeof adf === 'string') {
		try {
			adfObject = JSON.parse(adf);
		} catch {
			return [];
		}
	}

	const referencesByLocalId = new Map<string, SyncBlockReference>();
	traverse((adfObject || {}) as ADFEntity, {
		syncBlock: (node) => {
			const localId = node?.attrs?.localId;
			const resourceId = node?.attrs?.resourceId;
			if (typeof localId === 'string' && typeof resourceId === 'string') {
				referencesByLocalId.set(localId, { localId, resourceId });
			}
			return node;
		},
	});

	return Array.from(referencesByLocalId.values());
};

export const extractIncludedContentFromMacroOutput = (node: ADFEntity): ADFEntity[] | undefined => {
	const macroOutput = node?.attrs?.parameters?.macroOutput;
	if (typeof macroOutput !== 'string') {
		return undefined;
	}

	try {
		const parsedMacroOutput = JSON.parse(macroOutput) as ADFEntity;
		return (parsedMacroOutput.content || []) as ADFEntity[];
	} catch (error) {
		logger.error`Failed to parse included content macro output for TOC - ${error}`;
		return undefined;
	}
};

export const extractExcerptContent = (
	adfObject: ADFEntity,
	excerptName: string | undefined,
): ADFEntity[] => {
	const excerptContent: ADFEntity[] = [];
	let foundExcerpt = false;

	traverse((adfObject || {}) as ADFEntity, {
		bodiedExtension: (node) => {
			if (foundExcerpt || node?.attrs?.extensionKey !== EXCERPT_EXTENSION_KEY) {
				return;
			}

			const nodeExcerptName = getParamValue(getMacroParams(node), 'name');
			if (excerptName && nodeExcerptName !== excerptName) {
				return;
			}

			foundExcerpt = true;
			excerptContent.push(...((node.content || []) as ADFEntity[]));
		},
	});

	return excerptContent;
};

/**
 *
 * @param macroNode
 * @param adf - optional
 * @param includedContentMap - optional resolved ADF content for included pages/excerpts
 *
 * Used to handle options provided by adf to create the TOC
 */
export const handleTocNode = (
	macroNode: ADNode,
	adf: ADNode | string | null,
	includedContentMap?: IncludedContentMap,
	defaultSpaceKey?: string,
	syncBlockContentMap?: SyncBlockContentMap,
	includeSyncBlocks = false,
): {
	className: string;
	cssliststyle: any;
	csslistindent: any;
	separators: any;
	headerelements: ADFEntity[];
	headerRange: string[];
	hasbody: string;
	name: any;
	structure: any;
	outline: any;
	printable: any;
	macroId: any;
} => {
	const minConfig = Math.max(
		1,
		parseInt(macroNode?.attrs?.parameters?.macroParams?.minLevel?.value) || 1,
	);
	const maxConfig = Math.min(
		6,
		parseInt(macroNode?.attrs?.parameters?.macroParams?.maxLevel?.value) || 6,
	);
	const macroParams = macroNode?.attrs?.parameters?.macroParams;

	const includeheaderregex = macroParams?.include?.value;
	const excludeheaderregex = macroParams?.exclude?.value;

	// Helper function to go through heading content
	// and test regular expression against texts from content
	function filterHeadingWithRegex(node: ADFEntity) {
		if (!node?.content) {
			return false;
		}
		if (!includeheaderregex && !excludeheaderregex) {
			return true;
		}
		try {
			let includeRegex;
			if (includeheaderregex) {
				includeRegex = new RegExp(includeheaderregex);
			}
			let excludeRegex;
			if (excludeheaderregex) {
				excludeRegex = new RegExp(excludeheaderregex);
			}
			let fullHeaderString = '';
			for (const el of node?.content) {
				fullHeaderString +=
					el?.text || el?.attrs?.text || el?.attrs?.__confluenceMetadata?.contentTitle || '';
			}
			if (includeRegex && !includeRegex.test(fullHeaderString)) {
				return false;
			}
			if (excludeRegex && excludeRegex.test(fullHeaderString)) {
				return false;
			}
		} catch (error) {
			logger.error`Failed to handle regular expression - ${error}`;
		}

		return true;
	}
	// Hold a list of headers to look for duplicates
	// duplicated headers require an additional index/key which appends the link
	const headingsIds = new Set<string>();

	// NOTE: ADFNode and ADFEntity share the same shape but ADFEntity allows for more keys
	let adfObject = adf as ADFEntity;
	if (typeof adf === 'string') {
		try {
			adfObject = JSON.parse(adf);
		} catch {
			// Swallow the error as ADF format error handling is expected to be done earlier,
			// in the component that renders the entire content
		}
	}

	const adfWasString = typeof adf === 'string';
	const headerelements = buildHeadings(expVal('confluence_toc_render_perf', 'isEnabled', false), {
		adfObject,
		adfWasString,
		minConfig,
		maxConfig,
		includeheaderregex,
		excludeheaderregex,
		headingsIds,
		filterHeadingWithRegex,
		includedContentMap,
		defaultSpaceKey,
		syncBlockContentMap,
		includeSyncBlocks,
	});

	return {
		className: `${macroParams?.class?.value || 'macro-core'} toc-macro conf-macro output-block`,
		cssliststyle: macroParams?.style?.value,
		csslistindent: macroParams?.indent?.value,
		separators: macroParams?.separator?.value,
		headerelements,
		headerRange: ['H1', 'H2', 'H3', 'H4', 'H5', 'H6'].slice(minConfig - 1, maxConfig),
		hasbody: macroNode?.attrs.bodyType === 'none' ? 'false' : 'true',
		name: macroNode?.attrs?.extensionKey,
		structure: macroParams?.type?.value,
		outline: macroParams?.outline?.value,
		printable: macroParams?.printable?.value || 'true',
		macroId: macroNode?.attrs?.parameters?.macroMetadata?.macroId?.value,
	};
};

// Bundled traversal context for `buildHeadings`. Grouped into a single param to
// keep the function under the project's max-arity lint rule (7).
interface BuildHeadingsContext {
	adfObject: ADFEntity;
	adfWasString: boolean;
	minConfig: number;
	maxConfig: number;
	includeheaderregex: string | undefined;
	excludeheaderregex: string | undefined;
	headingsIds: Set<string>;
	filterHeadingWithRegex: (node: ADFEntity) => boolean;
	includedContentMap?: IncludedContentMap;
	defaultSpaceKey?: string;
	syncBlockContentMap?: SyncBlockContentMap;
	includeSyncBlocks?: boolean;
}

const buildHeadings = (
	usePerfPath: boolean,
	{
		adfObject,
		adfWasString,
		minConfig,
		maxConfig,
		includeheaderregex,
		excludeheaderregex,
		headingsIds,
		filterHeadingWithRegex,
		includedContentMap,
		defaultSpaceKey,
		syncBlockContentMap,
		includeSyncBlocks = false,
	}: BuildHeadingsContext,
): ADFEntity[] => {
	const headingElements: ADFEntity[] = [];
	let cachedHeadings: ADFEntity[] | undefined;
	let cacheBucket: Map<string, ADFEntity[]> | undefined;
	let cacheKey: string | undefined;
	const hasIncludedContent = Boolean(includedContentMap?.size);
	const hasSyncBlockContent = Boolean(syncBlockContentMap?.size);
	if (
		adfObject &&
		typeof adfObject === 'object' &&
		!adfWasString &&
		usePerfPath &&
		!hasIncludedContent &&
		!hasSyncBlockContent
	) {
		cacheKey = buildCacheKey(minConfig, maxConfig, includeheaderregex, excludeheaderregex);
		cacheBucket = headingsCache.get(adfObject);
		if (!cacheBucket) {
			cacheBucket = new Map();
			headingsCache.set(adfObject, cacheBucket);
		}
		cachedHeadings = cacheBucket.get(cacheKey);
	}

	if (cachedHeadings) {
		// Push cached entries; downstream code mutates the array but not its members.
		headingElements.push(...cachedHeadings);
		return headingElements;
	}

	const pushHeading = (node: ADFEntity, activeHeadingIds: Set<string>, parentMacroId?: string) => {
		if (filterHeadingWithRegex(node)) {
			let clone: ADFEntity;
			const headingId = getHeadingId(node, activeHeadingIds);
			const id = parentMacroId ? getCompositeHeadingId(parentMacroId, headingId) : headingId;
			if (usePerfPath) {
				// Shallow clone is sufficient: we only assign attrs.id, and downstream consumers
				// (MiniRenderer / renderContent) only READ node.content. This avoids the
				// expensive deep clone of the whole heading subtree
				clone = {
					...node,
					attrs: { ...node.attrs, id },
				};
			} else {
				// init deep-copy node
				clone = cloneDeep(node);
				clone.attrs = clone.attrs || {};
				clone.attrs.id = id;
			}
			if (node?.attrs?.level >= minConfig && node?.attrs?.level <= maxConfig) {
				headingElements.push(clone);
			}
		}
	};

	const getIncludedContentForNode = (node: ADFEntity): ADFEntity[] | undefined => {
		const macroOutputContent = extractIncludedContentFromMacroOutput(node);
		if (macroOutputContent) {
			return macroOutputContent;
		}
		const includedContentReference = extractIncludedContentReferenceFromNode(node, defaultSpaceKey);
		return includedContentReference
			? includedContentMap?.get(includedContentReference.key)
			: undefined;
	};

	// Create a reusable visitor collection that can handle both headings and extensions (LCMs)
	const createVisitors = (parentMacroId?: string, activeHeadingIds = headingsIds): any => ({
		heading: (node) => {
			pushHeading(node, activeHeadingIds, parentMacroId);
		},
		// Traverse into extension nodes (Legacy Content Macros) to find nested headings
		extension: (node) => {
			const includedContentReference = extractIncludedContentReferenceFromNode(
				node,
				defaultSpaceKey,
			);
			const includedContent = getIncludedContentForNode(node);
			const includedContentMacroId = includedContentReference?.macroId || getMacroId(node);
			if (includedContent && includedContentMacroId) {
				traverse(
					{ type: 'doc', content: includedContent } as ADFEntity,
					createVisitors(includedContentMacroId, new Set<string>()),
				);
			}

			const nestedContent = node?.attrs?.parameters?.nestedContent;
			if (nestedContent && typeof nestedContent === 'object') {
				// Recursively traverse the nested content with the same visitors
				// This allows us to handle nested LCMs (LCMs within LCMs)
				traverse(nestedContent as ADFEntity, createVisitors(parentMacroId));
			}
			return node;
		},
		bodiedExtension: (node) => {
			if (isIncludedContentNode(node)) {
				const includedContentReference = extractIncludedContentReferenceFromNode(
					node,
					defaultSpaceKey,
				);
				const includedContent = getIncludedContentForNode(node);
				const includedContentMacroId = includedContentReference?.macroId || getMacroId(node);
				if (includedContent && includedContentMacroId) {
					traverse(
						{ type: 'doc', content: includedContent } as ADFEntity,
						createVisitors(includedContentMacroId, new Set<string>()),
					);
				}
			}
			return node;
		},
		// Traverse into reference synced blocks to surface their headings. The
		// resolved content is provided by the data layer (`syncBlockContentMap`,
		// keyed by the node's `localId`). We use `localId` as the heading-id
		// prefix and a fresh dedup set per instance so the ids match what the
		// nested synced-block renderer emits (`headingIdPrefix={localId}`) and so
		// the same source block embedded twice gets distinct, stable ids.
		syncBlock: (node) => {
			if (!includeSyncBlocks) {
				return node;
			}
			const localId = node?.attrs?.localId;
			const syncBlockContent = localId ? syncBlockContentMap?.get(localId) : undefined;
			if (syncBlockContent && localId) {
				traverse(
					{ type: 'doc', content: syncBlockContent } as ADFEntity,
					createVisitors(localId, new Set<string>()),
				);
			}
			return node;
		},
	});

	// Traverse through ADF and filter out nodes that doesn't belong in the TOC
	// Options includes (minLevel, maxLevel, includeheaderregex, excludeheaderrregex)
	traverse((adfObject || {}) as ADFEntity, createVisitors());

	if (cacheBucket && cacheKey) {
		// Store a copy so subsequent mutations of the returned array don't pollute the cache.
		cacheBucket.set(cacheKey, headingElements.slice());
	}

	return headingElements;
};
